import { env } from "../lib/env"
import { tokenManager } from "../lib/token-manager"
import { logger } from "../lib/logger"
import { getMockOnboarding, preflightMockOnboarding, submitMockOnboarding } from "./onboarding-api.mock"
import type {
  OnboardingDecisions,
  OnboardingDetails,
  OnboardingValidationIssue,
  PreVerificationState,
  SubmitResult,
} from "./onboarding-api.types"
import type { Result, ApiError } from "../types/result"

async function fetchWithAuth(url: string, options: RequestInit): Promise<Result<Response>> {
  const makeHeaders = (token: string | null): HeadersInit => ({
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  })

  let response: Response
  try {
    response = await fetch(url, { ...options, headers: makeHeaders(tokenManager.getToken()) })
  } catch (err) {
    logger.error(new Error(`Network error fetching ${url}`, { cause: err }))
    return { ok: false, error: { type: "network" } }
  }

  if (response.status === 401) {
    let newToken: string | null
    try {
      newToken = await tokenManager.requestToken()
    } catch (err) {
      logger.error(new Error(`Token refresh failed for ${url}`, { cause: err }))
      return { ok: false, error: { type: "unauthorized" } }
    }
    try {
      response = await fetch(url, { ...options, headers: makeHeaders(newToken) })
    } catch (err) {
      logger.error(new Error(`Network error fetching ${url} (after token refresh)`, { cause: err }))
      return { ok: false, error: { type: "network" } }
    }
  }

  return { ok: true, data: response }
}

function classifyHttpError(status: number): ApiError {
  if (status === 401 || status === 403) return { type: "unauthorized" }
  if (status === 404) return { type: "not-found" }
  if (status >= 500) return { type: "server-error", status }
  return { type: "unexpected", status }
}

export const onboardingApi = {
  async getOnboarding(id: string): Promise<Result<OnboardingDetails>> {
    if (env.useMockData) {
      return { ok: true, data: await getMockOnboarding() }
    }

    const result = await fetchWithAuth(
      `${env.onboardingFormApiUrl}/api/onboardings/${id}`,
      {},
    )

    if (!result.ok) return result

    const response = result.data
    if (!response.ok) {
      if (response.status >= 500) {
        const body = await response.text()
        logger.error(new Error(`getOnboarding failed: HTTP ${response.status} for id ${id} — ${body}`))
      }
      return { ok: false, error: classifyHttpError(response.status) }
    }

    return { ok: true, data: await response.json() as OnboardingDetails }
  },

  /**
   * Reads the linked pre-verification again, without reloading the rest of the onboarding. `data: null` (204)
   * when there is no unfinished pre-verification to show.
   */
  async getPreVerification(id: string): Promise<Result<PreVerificationState | null>> {
    if (env.useMockData) {
      return { ok: true, data: null }
    }

    const result = await fetchWithAuth(
      `${env.onboardingFormApiUrl}/api/onboardings/${id}/pre-verification`,
      {},
    )

    if (!result.ok) return result

    const response = result.data
    if (response.status === 204) return { ok: true, data: null }

    if (!response.ok) {
      if (response.status >= 500) {
        const body = await response.text()
        logger.error(new Error(`getPreVerification failed: HTTP ${response.status} for id ${id} — ${body}`))
      }
      return { ok: false, error: classifyHttpError(response.status) }
    }

    return { ok: true, data: await response.json() as PreVerificationState }
  },

  /**
   * Has the service read the session's source again into the same session, after its pre-verification was
   * approved, and returns the updated onboarding. A 409 means the session was submitted or the source can't
   * be read.
   */
  async refreshSource(id: string): Promise<Result<OnboardingDetails>> {
    if (env.useMockData) {
      return { ok: true, data: await getMockOnboarding() }
    }

    const result = await fetchWithAuth(
      `${env.onboardingFormApiUrl}/api/onboardings/${id}/refresh-source`,
      { method: "POST" },
    )

    if (!result.ok) return result

    const response = result.data
    if (!response.ok) {
      if (response.status >= 500) {
        const body = await response.text()
        logger.error(new Error(`refreshSource failed: HTTP ${response.status} for id ${id} — ${body}`))
      }
      return { ok: false, error: classifyHttpError(response.status) }
    }

    return { ok: true, data: await response.json() as OnboardingDetails }
  },

  /**
   * Submits the onboarding form decisions.
   *
   * `ok: false` — transport or auth failure; no SubmitResult is available.
   * `ok: true`  — a SubmitResult was received and parsed. Callers must additionally
   *               check `result.data.success` for the business outcome. This includes
   *               409 (conflict) and 422 (step failure), which return a SubmitResult
   *               body the UI uses to display corrections.
   */
  /**
   * Asks what a submit with these decisions would be refused for, without writing anything.
   *
   * Takes the same body as submit so the answer describes the thing about to be committed rather than an
   * approximation of it. A failure here returns no issues rather than an error: preflight is advice, and
   * losing it must not stop a reviewer who would otherwise be able to proceed.
   */
  async preflightOnboarding(
    id: string,
    decisions: OnboardingDecisions,
  ): Promise<OnboardingValidationIssue[]> {
    if (env.useMockData) return preflightMockOnboarding(id, decisions)

    const result = await fetchWithAuth(
      `${env.onboardingFormApiUrl}/api/onboardings/${id}/preflight`,
      { method: "POST", body: JSON.stringify(decisions) },
    )

    if (!result.ok || !result.data.ok) return []

    return await result.data.json() as OnboardingValidationIssue[]
  },

  async submitOnboarding(
    id: string,
    decisions: OnboardingDecisions,
  ): Promise<Result<SubmitResult>> {
    if (env.useMockData) {
      return { ok: true, data: await submitMockOnboarding(id, decisions) }
    }

    const result = await fetchWithAuth(
      `${env.onboardingFormApiUrl}/api/onboardings/${id}/submit`,
      { method: "POST", body: JSON.stringify(decisions) },
    )

    if (!result.ok) return result

    const response = result.data

    // 409 (conflict / source already completed) and 422 (step failure with
    // corrections) both return a SubmitResult body — parse it so the UI can
    // display the appropriate message instead of returning an error.
    if (response.status === 409 || response.status === 422) {
      return { ok: true, data: await response.json() as SubmitResult }
    }

    if (!response.ok) {
      const body = await response.text()
      logger.error(new Error(`submitOnboarding failed: HTTP ${response.status} for id ${id} — ${body}`))
      return { ok: false, error: classifyHttpError(response.status) }
    }

    return { ok: true, data: await response.json() as SubmitResult }
  },
}
